import * as THREE from 'three';
import { z } from '@/lib/schemaConfig';

/** Bounded derived exterior, in site-local east/up/south metres. */
export const roofSurfaceSchema = z.object({
  version: z.literal(1),
  origin: z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]),
  positions: z.array(z.number().finite().min(-2000).max(2000)).min(9).max(60000),
  indices: z.array(z.number().int().nonnegative()).min(3).max(120000),
  rings: z.array(z.array(z.number().int().nonnegative()).min(3).max(4000)).min(1).max(256),
  wallBases: z.array(z.number().min(0).max(60)).max(256).optional(),
  source: z.object({
    title: z.string(), url: z.string().url(), collection: z.string(), retrievedAt: z.string(), acquired: z.string(),
    spacingM: z.number().positive(), coverage: z.number().min(0).max(1), points: z.number().int().positive(),
    groundSamples: z.number().int().positive(), groundSpreadM: z.number().nonnegative(),
    heightRangeM: z.tuple([z.number().positive(), z.number().positive()]),
    method: z.string(), review: z.string(), limitations: z.string(),
  }),
}).refine(s => s.positions.length % 3 === 0 && s.indices.length % 3 === 0 &&
  [...s.indices, ...s.rings.flat()].every(i => i < s.positions.length / 3) &&
  s.positions.every((v, i) => i % 3 !== 1 || (v > 0 && v < 60)) &&
  (!s.wallBases || (s.wallBases.length === s.rings.length && s.rings.every((ring,r)=>ring.every(i=>s.wallBases![r] < s.positions[i*3+1])))), 'Invalid roof mesh');

export type RoofSurface = z.infer<typeof roofSurfaceSchema>;

/** Highest roof triangle under a site-local point, including raised roof shelters.
 * Heights are above the roof's local ground datum, not WGS84 elevations. */
export function roofHeightAt(surface: RoofSurface, x: number, z: number, origin: {lat: number; lon: number}): number | null {
  if (!Number.isFinite(x + z) || !roofSurfaceSchema.safeParse(surface).success ||
      Math.abs(surface.origin[0] - origin.lon) > 1e-7 || Math.abs(surface.origin[1] - origin.lat) > 1e-7) return null;
  let highest: number | null = null;
  const p = surface.positions;
  for (let i = 0; i < surface.indices.length; i += 3) {
    const [a, b, c] = surface.indices.slice(i, i + 3).map(index => index * 3);
    const determinant = (p[b + 2] - p[c + 2]) * (p[a] - p[c]) + (p[c] - p[b]) * (p[a + 2] - p[c + 2]);
    if (Math.abs(determinant) < 1e-9) continue;
    const u = ((p[b + 2] - p[c + 2]) * (x - p[c]) + (p[c] - p[b]) * (z - p[c + 2])) / determinant;
    const v = ((p[c + 2] - p[a + 2]) * (x - p[c]) + (p[a] - p[c]) * (z - p[c + 2])) / determinant;
    const w = 1 - u - v;
    if (Math.min(u, v, w) < -1e-7) continue;
    const height = u * p[a + 1] + v * p[b + 1] + w * p[c + 1];
    highest = Math.max(highest ?? -Infinity, height);
  }
  return highest;
}

export function roofGeometries(surface: RoofSurface, origin: {lat: number; lon: number}) {
  const parsed = roofSurfaceSchema.safeParse(surface);
  if (!parsed.success) return null;
  if (Math.abs(surface.origin[0] - origin.lon) > 1e-7 || Math.abs(surface.origin[1] - origin.lat) > 1e-7) return null;
  const roof = new THREE.BufferGeometry();
  roof.setAttribute('position', new THREE.Float32BufferAttribute(surface.positions, 3));
  roof.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array(surface.positions.length / 3 * 2), 2));
  roof.setIndex(surface.indices);
  roof.computeVertexNormals();
  const wallPositions: number[] = [], outline: number[] = [];
  for (const [r, ring] of surface.rings.entries()) for (let j = 0; j < ring.length; j++) {
    const a = surface.positions.slice(ring[j] * 3, ring[j] * 3 + 3);
    const b = surface.positions.slice(ring[(j + 1) % ring.length] * 3, ring[(j + 1) % ring.length] * 3 + 3);
    const base = surface.wallBases?.[r] ?? 0;
    wallPositions.push(a[0],base,a[2], ...b, ...a, a[0],base,a[2], b[0],base,b[2], ...b);
    outline.push(...a, ...b);
  }
  const walls = new THREE.BufferGeometry();
  walls.setAttribute('position', new THREE.Float32BufferAttribute(wallPositions, 3));
  walls.computeVertexNormals();
  const perimeter = new THREE.BufferGeometry();
  perimeter.setAttribute('position', new THREE.Float32BufferAttribute(outline, 3));
  return {roof, walls, perimeter};
}

/** The lidar reference is local median ground, never an absolute scene elevation. */
export function roofBase(ground: {baseM: number; groundM: number[]} | undefined, derived: boolean): number {
  if (!ground) return 0;
  if (!derived) return ground.baseM;
  const sorted = ground.groundM.filter(Number.isFinite).sort((a,b) => a-b);
  return sorted.length ? (sorted[Math.floor((sorted.length-1)/2)] + sorted[Math.floor(sorted.length/2)]) / 2 : ground.baseM;
}
